'use client';

import Image from '@/components/Image';
import MagicJump, { type JumpTarget } from '@/components/MagicJump';
import { useValidExposure } from '@/hooks/useValidExposure';
import logger from '@/utils/client-logger';

import type {
  OneRowFourNavItem,
  OneRowFourNavProps,
  OneRowFourNavValidExposureParams,
} from './types';

const MAX_NAV_ITEMS = 5;
const MODULE_NAME = '1行4图文导航';

function OneRowFourNavItemView({
  item,
  index,
  moduleId,
  moduleIndex,
  moduleTitle,
  onTrack,
  onValidExpTrack,
}: {
  item: OneRowFourNavItem & { imageSrc: string };
  index: number;
  moduleId: string;
  moduleIndex: number;
  moduleTitle: string;
} & Pick<OneRowFourNavProps, 'onTrack' | 'onValidExpTrack'>) {
  const trackingParams: OneRowFourNavValidExposureParams = {
    module_index: moduleIndex,
    module_id: moduleId,
    module_title: moduleTitle,
    module_name: MODULE_NAME,
    module_tab_name: '',
    mkt_index: index,
    mkt_index_sub: '',
    mkt_jump_type: String(item.jump_type ?? ''),
    mkt_jump_content: String(item.Jump_target ?? ''),
  };
  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });
  const jumpTarget: JumpTarget = {
    jump_type: item.jump_type,
    Jump_target: item.Jump_target ?? undefined,
  };
  const imageAlt = item.title
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const handleClick = () => {
    if (!onTrack) return;

    try {
      onTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC OneRowFourNav click tracking error:', error);
    }
  };

  return (
    <li ref={validExposureRef} className="flex min-w-0 items-center justify-center">
      <MagicJump
        prefetch
        item={jumpTarget}
        onBeforeJump={handleClick}
        aria-label={imageAlt}
        className="flex min-w-0 items-center justify-center gap-[6px]"
      >
        <span className="flex size-[24px] shrink-0 items-center justify-center" aria-hidden>
          <Image
            src={item.imageSrc}
            alt={imageAlt}
            width={24}
            height={24}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            className="block size-full object-contain"
          />
        </span>
        <span
          className="whitespace-nowrap font-br text-14 text-primary-black"
          style={item.text_color ? { color: item.text_color } : undefined}
        >
          {item.title}
        </span>
      </MagicJump>
    </li>
  );
}

export default function OneRowFourNav({
  indexData,
  moduleIndex,
  onTrack,
  onValidExpTrack,
}: OneRowFourNavProps) {
  // 新装修协议使用 content；同时兼容 Block Init 归一化后的扁平结构。
  const content = indexData.content ? { ...indexData, ...indexData.content } : indexData;
  const navigationItems = (content.jump_target || content.items || [])
    .map((item) => ({ ...item, imageSrc: item.jump_image || item.image || '' }))
    .filter((item) => item.imageSrc && item.title)
    .slice(0, MAX_NAV_ITEMS);

  if (navigationItems.length === 0) return null;

  const moduleId = String(content.blocks_advances_id ?? content.id ?? '');
  const moduleTitle = content.layout_title || '';

  return (
    <div
      data-component-id={indexData.component_id}
      className="w-full px-[24px] py-[8px]"
      style={{ backgroundColor: content.bg_color || '#f2f3f5' }}
    >
      <ul
        className="mx-auto grid h-[48px] w-full max-w-[1440px]"
        style={{ gridTemplateColumns: `repeat(${navigationItems.length}, minmax(0, 1fr))` }}
      >
        {navigationItems.map((item, index) => (
          <OneRowFourNavItemView
            key={item.id || `${item.title}-${index}`}
            item={item}
            index={index}
            moduleId={moduleId}
            moduleIndex={moduleIndex}
            moduleTitle={moduleTitle}
            onTrack={onTrack}
            onValidExpTrack={onValidExpTrack}
          />
        ))}
      </ul>
    </div>
  );
}
